import { useState, useEffect } from 'react';
import './ReasonModal.css';

const COMMON_REASONS = {
  reject: [
    'Contains inappropriate language',
    'Spam or promotional content',
    'Not relevant to the product',
    'Violates community guidelines',
    'Duplicate review'
  ],
  flag: [
    'Potential legal issue - needs supervisor review',
    'Ambiguous content - needs clarification',
    'Suspected fake review',
    'Copyright violation',
    'Requires additional verification'
  ],
  unpublish: [
    'Approved by mistake',
    'Content became inappropriate after approval',
    'Customer requested removal',
    'Legal/compliance requirement',
    'Product recall or safety issue'
  ]
};

/**
 * Modal for entering moderation action reasons
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {string} props.actionType - 'reject' | 'flag' | 'unpublish'
 * @param {string} props.title - Modal title
 * @param {Function} props.onConfirm - Called with reason string
 * @param {Function} props.onCancel - Called when modal is cancelled
 * @param {boolean} props.isLoading - Show loading state
 */
function ReasonModal({
  isOpen,
  actionType,
  title,
  onConfirm,
  onCancel,
  isLoading = false
}) {
  const [reason, setReason] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [error, setError] = useState('');

  const commonReasons = COMMON_REASONS[actionType] || [];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setUseCustom(false);
      setError('');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onCancel]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate reason
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setError('Reason must be at least 3 characters');
      return;
    }
    if (trimmedReason.length > 500) {
      setError('Reason must not exceed 500 characters');
      return;
    }

    setError('');
    onConfirm(trimmedReason);
  };

  const handleSelectCommon = (commonReason) => {
    setReason(commonReason);
    setUseCustom(false);
    setError('');
  };

  const handleCustomClick = () => {
    setReason('');
    setUseCustom(true);
  };

  const handleClose = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const getButtonClass = () => {
    switch (actionType) {
      case 'reject':
      case 'unpublish':
        return 'btn-danger';
      case 'flag':
        return 'btn-warning';
      default:
        return 'btn-primary';
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Common reasons */}
            <div className="common-reasons">
              <label>Select a reason:</label>
              <div className="reason-options">
                {commonReasons.map((r, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`reason-option ${reason === r && !useCustom ? 'selected' : ''}`}
                    onClick={() => handleSelectCommon(r)}
                    disabled={isLoading}
                  >
                    {r}
                  </button>
                ))}
                <button
                  type="button"
                  className={`reason-option reason-option-custom ${useCustom ? 'selected' : ''}`}
                  onClick={handleCustomClick}
                  disabled={isLoading}
                >
                  Other (specify)
                </button>
              </div>
            </div>

            {/* Custom reason textarea */}
            {useCustom && (
              <div className="custom-reason">
                <label htmlFor="reason-input">Enter your reason:</label>
                <textarea
                  id="reason-input"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter reason (3-500 characters)"
                  rows={4}
                  maxLength={500}
                  autoFocus
                  disabled={isLoading}
                />
                <div className="char-count">
                  {reason.length}/500 characters
                </div>
              </div>
            )}

            {/* Error message */}
            {error && <div className="modal-error">{error}</div>}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn ${getButtonClass()}`}
              disabled={isLoading || !reason.trim()}
            >
              {isLoading ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReasonModal;
